import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { solarAPI } from '../../services/api';
import { SolarPanel } from '../../types';
import { Alert } from '../common/Alert';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

type PanelFormData = Omit<SolarPanel, 'id' | 'createdAt' | 'updatedAt'>;

export const CreatePanel: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<PanelFormData>();

  const onSubmit = async (data: PanelFormData) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await solarAPI.create(data);
      setSuccess(true);
      reset();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create panel');
    } finally {
      setLoading(false);
    }
  };

  const inputSections = [
    {
      title: 'Basic Information',
      fields: [
        { name: 'SKU', label: 'SKU', type: 'text', required: true },
        { name: 'Description', label: 'Description', type: 'text', required: true },
        { name: 'CellType', label: 'Cell Type', type: 'text', required: true },
        { name: 'ConnectorType', label: 'Connector Type', type: 'text', required: true }
      ]
    },
    {
      title: 'Power Specifications',
      fields: [
        { name: 'Watts_W', label: 'Power (W)', type: 'number', required: true },
        { name: 'Pmax_W', label: 'Pmax (W)', type: 'number', required: true },
        { name: 'Vmp_V', label: 'Vmp (V)', type: 'number', step: '0.01', required: true },
        { name: 'Imp_A', label: 'Imp (A)', type: 'number', step: '0.01', required: true },
        { name: 'Voc_V', label: 'Voc (V)', type: 'number', step: '0.01', required: true },
        { name: 'Isc_A', label: 'Isc (A)', type: 'number', step: '0.01', required: true }
      ]
    },
    {
      title: 'Physical Dimensions',
      fields: [
        { name: 'Length_mm', label: 'Length (mm)', type: 'number', required: true },
        { name: 'Width_mm', label: 'Width (mm)', type: 'number', required: true },
        { name: 'Thickness_mm', label: 'Thickness (mm)', type: 'number', required: true },
        { name: 'Weight_kg', label: 'Weight (kg)', type: 'number', step: '0.1', required: true },
        { name: 'NumberOfCells_count', label: 'Number of Cells', type: 'number', required: true },
        { name: 'CableLength_mm', label: 'Cable Length (mm)', type: 'number', required: true }
      ]
    },
    {
      title: 'Logistics & Performance',
      fields: [
        { name: 'PanelsPerPallet_count', label: 'Panels per Pallet', type: 'number', required: true },
        { name: 'PalletsPerTruck_count', label: 'Pallets per Truck', type: 'number', required: true },
        { name: 'SnowLoad_Pa', label: 'Snow Load (Pa)', type: 'number', required: true },
        { name: 'WindLoad_Pa', label: 'Wind Load (Pa)', type: 'number', required: true },
        { name: 'ModuleEfficiency_percent', label: 'Module Efficiency (%)', type: 'number', step: '0.01', required: true },
        { name: 'TempCoeffPmax_percent_per_C', label: 'Temp Coeff Pmax (%/°C)', type: 'number', step: '0.01', required: true },
        { name: 'TempCoeffVoc_percent_per_C', label: 'Temp Coeff Voc (%/°C)', type: 'number', step: '0.01', required: true },
        { name: 'TempCoeffIsc_percent_per_C', label: 'Temp Coeff Isc (%/°C)', type: 'number', step: '0.01', required: true }
      ]
    },
    {
      title: 'Additional Information',
      fields: [
        { name: 'remarks', label: 'Remarks', type: 'text', required: false }
      ]
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto"
    >
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Create New Solar Panel</h1>

      {success && (
        <Alert
          type="success"
          message="Solar panel created successfully!"
          onClose={() => setSuccess(false)}
        />
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError('')}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {inputSections.map((section, sectionIndex) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: sectionIndex * 0.1 }}
            className="bg-white p-6 rounded-lg shadow-md"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map((field) => (
                <Input
                  key={field.name}
                  label={field.label}
                  type={field.type}
                  step={field.step}
                  {...register(field.name as keyof PanelFormData, {
                    required: field.required ? `${field.label} is required` : false,
                    valueAsNumber: field.type === 'number'
                  })}
                  error={errors[field.name as keyof PanelFormData]?.message}
                />
              ))}
            </div>
          </motion.div>
        ))}

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => reset()}
          >
            Reset Form
          </Button>
          <Button
            type="submit"
            loading={loading}
            size="lg"
          >
            Create Panel
          </Button>
        </div>
      </form>
    </motion.div>
  );
};